import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Music, Camera, Flower2, UtensilsCrossed, Sparkles } from 'lucide-react';

const eventHighlights = [
  {
    icon: Flower2,
    title: 'Kalyanam',
    description: 'Traditional wedding ceremony with sacred rituals, lamp lighting, and garland exchange',
    image: 'https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    color: 'text-yellow-600'
  },
  {
    icon: UtensilsCrossed,
    title: 'Sadya Feast',
    description: 'Authentic Kerala wedding feast served on banana leaf with 20+ traditional dishes',
    image: 'https://images.pexels.com/photos/14132112/pexels-photo-14132112.jpeg?auto=compress&cs=tinysrgb&w=800',
    color: 'text-emerald-600'
  },
  {
    icon: Music,
    title: 'Chenda Melam',
    description: 'Live traditional percussion performance that sets the festive mood',
    image: 'https://images.unsplash.com/photo-1558247102-3cd1995b1e6b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    color: 'text-yellow-700'
  },
  {
    icon: Sparkles,
    title: 'Traditional Music & Dance',
    description: 'Classical Kerala music and dance performances throughout the event',
    image: 'https://images.unsplash.com/photo-1699163104435-4a1d618043b1?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    color: 'text-emerald-700'
  },
  {
    icon: Camera,
    title: 'Photo Sessions',
    description: 'Beautiful photo zones with traditional Kerala décor and props',
    image: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    color: 'text-yellow-600'
  },
  {
    icon: Heart,
    title: 'Cultural Immersion',
    description: 'Experience Kerala traditions, customs, and warm hospitality',
    image: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    color: 'text-emerald-600'
  }
];

const EventHighlightsSection = () => {
  return (
    <section className="py-24 md:py-32 px-6 bg-white" data-testid="event-highlights-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-normal text-emerald-900 mb-4" data-testid="highlights-title">
            What to Expect
          </h2>
          <p className="text-lg text-stone-600" data-testid="highlights-subtitle">
            Every element of an authentic Kerala wedding celebration
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {eventHighlights.map((highlight, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative overflow-hidden rounded-3xl hover-scale cursor-pointer"
              data-testid={`highlight-card-${index}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={highlight.image}
                  alt={highlight.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/90 via-emerald-900/40 to-transparent flex flex-col justify-end p-6">
                <highlight.icon className={`w-10 h-10 ${highlight.color} mb-3`} />
                <h3 className="text-2xl font-medium text-white mb-2">
                  {highlight.title}
                </h3>
                <p className="text-stone-200 text-sm">{highlight.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EventHighlightsSection;
